/**
 * AccessBanner Component
 * Displays the user's access level to content as a banner
 * Shows different messages based on access level (sharedOwner, adminOverride, none)
 * Hidden for main owners (they don't need to see their own access level)
 */

import React from 'react';
import { Alert, Tag, Space, Tooltip } from 'antd';
import { 
  ShareAltOutlined, 
  EyeOutlined, 
  CrownOutlined,
  SafetyCertificateOutlined,
  LockOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { AccessLevel } from '../../../services/ownershipService';
import styles from './AccessBanner.module.css';

/**
 * Maps access levels to banner configuration
 */
const accessLevelConfig = {
  [AccessLevel.SHARED_OWNER]: {
    type: 'info',
    icon: <ShareAltOutlined />,
    tagColor: 'blue',
    messageKey: 'ownership.sharedAccess',
    descriptionKey: 'ownership.sharedAccessDesc',
  },
  [AccessLevel.ADMIN_OVERRIDE]: {
    type: 'warning',
    icon: <SafetyCertificateOutlined />,
    tagColor: 'gold',
    messageKey: 'ownership.adminAccess',
    descriptionKey: 'ownership.adminAccessDesc',
  },
  [AccessLevel.NONE]: {
    type: 'default',
    icon: <EyeOutlined />,
    tagColor: 'default',
    messageKey: 'ownership.viewOnly',
    descriptionKey: 'ownership.viewOnlyDesc',
  },
};

/**
 * Access banner component showing user's permission level
 * @param {Object} props - Component props
 * @param {string} props.accessLevel - Access level ('none', 'sharedowner', 'mainowner', 'adminoverride')
 * @param {Object} [props.ownerInfo] - Information about the main owner
 * @param {boolean} [props.canEdit=false] - Whether the user can edit
 * @param {boolean} [props.canDelete=false] - Whether the user can delete
 * @param {boolean} [props.showPermissions=true] - Show edit/delete permission tags
 * @param {string} [props.className] - Additional CSS class
 */
export function AccessBanner({ 
  accessLevel, 
  ownerInfo,
  canEdit = false,
  canDelete = false,
  showPermissions = true,
  className = ''
}) {
  const { t } = useTranslation();
  
  // Normalize access level to lowercase
  const normalizedLevel = accessLevel?.toLowerCase() || AccessLevel.NONE;
  
  // Don't show banner for main owner
  if (normalizedLevel === AccessLevel.MAIN_OWNER) {
    return null;
  }

  const config = accessLevelConfig[normalizedLevel] || accessLevelConfig[AccessLevel.NONE];

  const renderPermissionTags = () => {
    if (!showPermissions) return null;
    
    if (canEdit || canDelete) {
      return (
        <Space size="small" className={styles.permissionTags}>
          {canEdit && (
            <Tag color="green" icon={<CrownOutlined />}>
              {t('ownership.canEdit')}
            </Tag>
          )}
          {canDelete && (
            <Tag color="orange" icon={<CrownOutlined />}>
              {t('ownership.canDelete')}
            </Tag>
          )}
        </Space>
      );
    }
    
    return (
      <Tag color="default" icon={<LockOutlined />}>
        {t('ownership.readOnly')}
      </Tag>
    );
  };

  const ownerDisplay = ownerInfo?.displayName || ownerInfo?.email || t('ownership.unknownOwner');

  const description = (
    <div className={styles.bannerContent}>
      <span>{t(config.descriptionKey)}</span>
      {ownerInfo && (
        <Tooltip title={t('ownership.ownedBy')}>
          <span className={styles.ownerInfo}>
            {t('ownership.ownedByLabel')}: <strong>{ownerDisplay}</strong>
          </span>
        </Tooltip>
      )}
      {showPermissions && renderPermissionTags()}
    </div>
  );

  return (
    <Alert
      className={`${styles.accessBanner} ${className}`.trim()}
      type={config.type}
      banner
      showIcon
      icon={config.icon}
      message={
        <span className={styles.message}>
          <Tag color={config.tagColor}>{t(config.messageKey)}</Tag>
        </span>
      }
      description={description}
    />
  );
}

AccessBanner.propTypes = {
  accessLevel: PropTypes.string.isRequired,
  ownerInfo: PropTypes.shape({
    id: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  showPermissions: PropTypes.bool,
  className: PropTypes.string,
};

export default AccessBanner;
